import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { InputText, type InputTextProps } from 'primereact/inputtext';
import {
  PasswordStrengthIndicator,
  type PasswordValidationTexts,
  type PasswordValidationState,
} from './PasswordStrengthIndicator';
import { ReactComponent as EyeOpened } from '../../../assets/shared/ic-shared-eye-opened.svg';
import { ReactComponent as EyeClosed } from '../../../assets/shared/ic-shared-eye-closed.svg';
import { LiliFloatingLabel } from '../LiliFloatingLabel/LiliFloatingLabel';
import type { LiliInputBaseProps } from '../../types/LiliInputBaseProps';
import { LiliToolTipColor, LiliTooltip, TooltipPosition } from '../LiliTooltip';
import { textInputStyle } from '../../util/styling';
import { useFontClass } from '../../tokens';

export interface LiliPasswordInputProps
  extends LiliInputBaseProps, Omit<InputTextProps, 'id' | 'onBlur' | 'onFocus' | 'onChange'> {
  onChange: (input: string) => void;
  styles?: string;
  passwordValidationTexts?: PasswordValidationTexts;
  inilineChild?: React.ReactNode;
  initialValue?: string;
}

export interface LiliPasswordInputHandle {
  /** Re-masks the field after the user has revealed it. */
  hidePassword: () => void;
}

export const LiliPasswordInput = forwardRef<LiliPasswordInputHandle, LiliPasswordInputProps>(
  (
    {
      onChange,
      onBlur,
      errorMessage,
      inilineChild,
      styles,
      title,
      passwordValidationTexts,
      disabled,
      tabIndex,
      id,
      initialValue,
      hideError,
      ...otherProps
    },
    ref
  ) => {
    const [passwordInput, setPasswordInput] = useState<string | undefined>(initialValue);
    const [inputType, setInputType] = useState<'text' | 'password'>('password');
    const [isFocused, setIsFocused] = useState(false);
    const [validationState, setValidationState] = useState<PasswordValidationState>({
      isLengthValid: false,
      isAtLeastOneCapital: false,
      isAtLeastOneLowerCase: false,
      isAtLeastOneNumber: false,
    });
    const inputRef = useRef<HTMLInputElement>(null);
    const isFirstRenderRef = useRef(true);
    const { fontClass } = useFontClass();

    useImperativeHandle(ref, () => ({
      hidePassword: () => setInputType('password'),
    }));

    const onInputChange = (value: string) => {
      setPasswordInput(value);

      setValidationState({
        isLengthValid: value.length >= 8,
        isAtLeastOneCapital: /[A-Z]/.test(value),
        isAtLeastOneLowerCase: /[a-z]/.test(value),
        isAtLeastOneNumber: /[0-9]/.test(value),
      });

      onChange(value);
    };

    useEffect(() => {
      if (!isFirstRenderRef.current && inputRef.current) {
        const length = inputRef.current.value.length;
        setTimeout(() => {
          // setTimeout is used to ensure the cursor position is set after the DOM updates.
          // This ensures the cursor is positioned correctly at the end of the text.
          inputRef.current?.focus();
          inputRef.current?.setSelectionRange(length, length);
        }, 0);
      }
      isFirstRenderRef.current = false;
    }, [inputType]);

    return (
      <form onSubmit={(event) => event.preventDefault()} className={`relative ${styles || ''}`}>
        <LiliFloatingLabel
          errorMessage={errorMessage}
          label={title}
          inilineChild={inilineChild}
          disabled={disabled}
          id={id}
          hideError={hideError}>
          <div className='flex flex-row items-baseline mt-[6px]'>
            {/* Full API: https://primereact.org/inputtext/ */}
            <InputText
              ref={inputRef}
              type={inputType}
              className={textInputStyle(fontClass, inputRef?.current?.value, disabled)}
              unstyled
              placeholder=' '
              id={id}
              onChange={(event) => {
                onInputChange(event.target.value);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                setIsFocused(false);
                onBlur?.();
              }}
              value={passwordInput}
              disabled={disabled}
              tabIndex={tabIndex}
              data-tooltip-id={
                !!passwordValidationTexts ? 'password-strength-indicator' : undefined
              }
              data-private
              {...otherProps}
            />
            {!disabled && (
              <button
                id={`showHidePassword-${id}`}
                className='mb-[6px]'
                type='button'
                onClick={() => setInputType(inputType === 'password' ? 'text' : 'password')}>
                {inputType === 'password' ? (
                  <EyeOpened className='text-text-main-secondary' />
                ) : (
                  <EyeClosed className='text-text-main-secondary' />
                )}
              </button>
            )}
          </div>
        </LiliFloatingLabel>

        {/* Password strength indicator tooltip.
          Mount only while focused: react-tooltip v6 does not reliably re-run its positioning
          pipeline when a controlled tooltip remounts via `hidden`, which could leave it stuck
          unpositioned (covering the field). A fresh mount per focus always positions correctly. */}
        {passwordValidationTexts && !disabled && isFocused && (
          <LiliTooltip
            description=''
            targetId='password-strength-indicator'
            position={TooltipPosition.BOTTOM_START}
            color={LiliToolTipColor.White}
            isOpen={isFocused}
            className='!translate-x-[-15px] !translate-y-[5px]'
            customContent={
              <div className='py-[12px] px-[16px]'>
                <PasswordStrengthIndicator
                  validationState={validationState}
                  validationTexts={passwordValidationTexts}
                />
              </div>
            }
          />
        )}
      </form>
    );
  }
);

LiliPasswordInput.displayName = 'LiliPasswordInput';
